// SVG diagrams for the Infra overview: a currency donut, a module treemap, an
// app → module flow diagram and a module composition graph. Sized to their
// container in pixels (no viewBox scaling) so text stays at UI size.

import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { getColors, PaletteMode } from '../../theme/tokens';
import {
  AppModuleFlows,
  Lag,
  LAG_ORDER,
  ModuleEdge,
  moduleName,
  modulePath,
  ModuleSummary,
  OTHER_MODULES,
} from '../../lib/fleetModules';
import { LAG_HUES, LAG_LABELS, Legend } from './charts';

const FALLBACK_WIDTH = 720;

/** The element's content width, tracked with ResizeObserver where available. */
const useWidth = (): [React.RefObject<HTMLDivElement | null>, number] => {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    setWidth(el.clientWidth);
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(entries => setWidth(entries[0].contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width || FALLBACK_WIDTH];
};

const truncate = (s: string, max: number) => (s.length > max ? `${s.slice(0, max - 1)}…` : s);

// ---------------------------------------------------------------------------
// Donut: direct calls by lag
// ---------------------------------------------------------------------------

const arcPath = (cx: number, cy: number, r: number, inner: number, a0: number, a1: number) => {
  const p = (rad: number, a: number) => [cx + rad * Math.sin(a), cy - rad * Math.cos(a)];
  const large = a1 - a0 > Math.PI ? 1 : 0;
  const [x0, y0] = p(r, a0);
  const [x1, y1] = p(r, a1);
  const [x2, y2] = p(inner, a1);
  const [x3, y3] = p(inner, a0);
  return `M${x0},${y0} A${r},${r} 0 ${large} 1 ${x1},${y1} L${x2},${y2} A${inner},${inner} 0 ${large} 0 ${x3},${y3} Z`;
};

export const LagDonut: React.FC<{ counts: Record<Lag, number> }> = ({ counts }) => {
  const theme = useTheme();
  const mode = theme.palette.mode;
  const c = getColors(mode);
  const hues = LAG_HUES[mode];
  const lags = LAG_ORDER.filter(l => l !== 'unknown' || counts.unknown > 0);
  const total = lags.reduce((s, l) => s + counts[l], 0);
  const size = 180;
  const r = size / 2 - 2;
  const inner = r - 22;

  let angle = 0;
  const arcs = lags
    .filter(l => counts[l] > 0)
    .map(l => {
      const sweep = total > 0 ? (counts[l] / total) * Math.PI * 2 : 0;
      // A lone segment is a full ring; an SVG arc can't start and end on the same point.
      const a1 = angle + Math.min(sweep, Math.PI * 2 - 0.0001);
      const arc = { lag: l, d: arcPath(size / 2, size / 2, r, inner, angle, a1) };
      angle += sweep;
      return arc;
    });
  const share = total > 0 ? Math.round((counts.current / total) * 100) : 0;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
      <svg width={size} height={size} role="img" aria-label={`${share}% of direct calls on the latest release`}>
        {total === 0 && <circle cx={size / 2} cy={size / 2} r={(r + inner) / 2} fill="none" stroke={c.hairline} strokeWidth={r - inner} />}
        {arcs.map(a => (
          <Tooltip
            key={a.lag}
            arrow
            title={`${LAG_LABELS[a.lag]}: ${counts[a.lag]} (${Math.round((counts[a.lag] / total) * 100)}%)`}
          >
            <path d={a.d} fill={hues[a.lag]} stroke={c.surface} strokeWidth={2} style={{ cursor: 'default' }} />
          </Tooltip>
        ))}
        <text x={size / 2} y={size / 2 + 2} textAnchor="middle" fontSize={30} fontWeight={700} fill={c.ink}>
          {share}%
        </text>
        <text x={size / 2} y={size / 2 + 22} textAnchor="middle" fontSize={12} fill={c.inkMuted}>
          on latest
        </text>
      </svg>
      <Box sx={{ minWidth: 160, '& > div': { flexDirection: 'column', mt: 0 } }}>
        <Legend
          items={lags.map(l => ({
            label: LAG_LABELS[l],
            color: hues[l],
            value: `${counts[l]} · ${total > 0 ? Math.round((counts[l] / total) * 100) : 0}%`,
          }))}
        />
      </Box>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Treemap: module footprint
// ---------------------------------------------------------------------------

export interface TreeRect<T> {
  x: number;
  y: number;
  w: number;
  h: number;
  item: T;
}

/** Squarified treemap layout (Bruls, Huizing, van Wijk). */
export const squarify = <T,>(
  input: Array<{ value: number; item: T }>,
  x: number,
  y: number,
  w: number,
  h: number
): Array<TreeRect<T>> => {
  const items = input.filter(i => i.value > 0).sort((a, b) => b.value - a.value);
  const sum = items.reduce((s, i) => s + i.value, 0);
  if (sum === 0 || w <= 0 || h <= 0) return [];
  const scale = (w * h) / sum;
  const areas = items.map(i => ({ area: i.value * scale, item: i.item }));
  const out: Array<TreeRect<T>> = [];
  const rect = { x, y, w, h };

  const worst = (row: number[], side: number) => {
    const s = row.reduce((a, b) => a + b, 0);
    const max = Math.max(...row);
    const min = Math.min(...row);
    return Math.max((side * side * max) / (s * s), (s * s) / (side * side * min));
  };
  const place = (row: typeof areas) => {
    const s = row.reduce((a, r) => a + r.area, 0);
    if (rect.w >= rect.h) {
      const colW = s / rect.h;
      let yy = rect.y;
      row.forEach(r => {
        const hh = r.area / colW;
        out.push({ x: rect.x, y: yy, w: colW, h: hh, item: r.item });
        yy += hh;
      });
      rect.x += colW;
      rect.w -= colW;
    } else {
      const rowH = s / rect.w;
      let xx = rect.x;
      row.forEach(r => {
        const ww = r.area / rowH;
        out.push({ x: xx, y: rect.y, w: ww, h: rowH, item: r.item });
        xx += ww;
      });
      rect.y += rowH;
      rect.h -= rowH;
    }
  };

  let row: typeof areas = [];
  areas.forEach(a => {
    const side = Math.min(rect.w, rect.h);
    const cur = row.map(r => r.area);
    if (row.length === 0 || worst([...cur, a.area], side) <= worst(cur, side)) {
      row.push(a);
    } else {
      place(row);
      row = [a];
    }
  });
  if (row.length) place(row);
  return out;
};

/** Share-on-latest ramp, low → high; the strong end is the most current. */
const SHARE_RAMP: Record<PaletteMode, Array<{ fill: string; text: 'ink' | 'light' }>> = {
  light: [
    { fill: '#cde2fb', text: 'ink' },
    { fill: '#9ec5f4', text: 'ink' },
    { fill: '#5598e7', text: 'ink' },
    { fill: '#2a78d6', text: 'light' },
    { fill: '#104281', text: 'light' },
  ],
  dark: [
    { fill: '#173a63', text: 'light' },
    { fill: '#1c5cab', text: 'light' },
    { fill: '#2a78d6', text: 'light' },
    { fill: '#6da7ec', text: 'ink' },
    { fill: '#b7d3f6', text: 'ink' },
  ],
};
const SHARE_STEPS = ['0–20%', '20–40%', '40–60%', '60–80%', '80–100%'];

const shareStep = (m: ModuleSummary) => {
  const share = m.directUsages > 0 ? m.lagCounts.current / m.directUsages : 1;
  return Math.min(4, Math.floor(share * 5));
};

export const ModuleTreemap: React.FC<{ modules: ModuleSummary[]; height?: number; max?: number }> = ({
  modules,
  height = 340,
  max = 40,
}) => {
  const theme = useTheme();
  const mode = theme.palette.mode;
  const c = getColors(mode);
  const navigate = useNavigate();
  const [ref, width] = useWidth();
  const ramp = SHARE_RAMP[mode];

  const rects = useMemo(
    () =>
      squarify(
        modules.slice(0, max).map(m => ({ value: m.jobs.length, item: m })),
        0,
        0,
        width,
        height
      ),
    [modules, max, width, height]
  );

  return (
    <Box ref={ref}>
      <svg width={width} height={height} role="img" aria-label="Modules sized by jobs using them">
        {rects.map(({ x, y, w, h, item: m }) => {
          const step = ramp[shareStep(m)];
          const ink = step.text === 'ink' ? '#0b0b0b' : '#ffffff';
          const showName = w > 64 && h > 26;
          const showCount = w > 64 && h > 44;
          const maxChars = Math.floor((w - 12) / 6.4);
          return (
            <Tooltip
              key={m.source}
              arrow
              followCursor
              title={
                <span>
                  <b>{m.name}</b>
                  <br />
                  {m.jobs.length} jobs · {m.apps.length} apps · {m.versions.length} versions
                  <br />
                  {m.directUsages > 0
                    ? `${Math.round((m.lagCounts.current / m.directUsages) * 100)}% of direct calls on ${m.newest}${m.latestSource === 'deployed' ? ' (newest deployed)' : ''}`
                    : 'nested calls only'}
                </span>
              }
            >
              <g style={{ cursor: 'pointer' }} onClick={() => navigate(modulePath(m.source))}>
                <rect
                  x={x + 1}
                  y={y + 1}
                  width={Math.max(0, w - 2)}
                  height={Math.max(0, h - 2)}
                  rx={4}
                  fill={step.fill}
                  stroke={c.surface}
                  strokeWidth={0}
                />
                {showName && (
                  <text x={x + 8} y={y + 18} fontSize={12} fontWeight={600} fill={ink} pointerEvents="none">
                    {truncate(m.name, maxChars)}
                  </text>
                )}
                {showCount && (
                  <text x={x + 8} y={y + 34} fontSize={11} fill={ink} opacity={0.85} pointerEvents="none">
                    {m.jobs.length} jobs
                  </text>
                )}
              </g>
            </Tooltip>
          );
        })}
      </svg>
      <Legend items={ramp.map((s, i) => ({ label: SHARE_STEPS[i], color: s.fill }))} />
      <Typography variant="caption" sx={{ color: c.inkFaint }}>
        Share of direct calls on the module's latest release in Spacelift
      </Typography>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Flow diagram: apps → modules
// ---------------------------------------------------------------------------

const band = (x0: number, y0a: number, y0b: number, x1: number, y1a: number, y1b: number) => {
  const xm = (x0 + x1) / 2;
  return `M${x0},${y0a} C${xm},${y0a} ${xm},${y1a} ${x1},${y1a} L${x1},${y1b} C${xm},${y1b} ${xm},${y0b} ${x0},${y0b} Z`;
};

export const AppModuleFlow: React.FC<{ flows: AppModuleFlows }> = ({ flows }) => {
  const theme = useTheme();
  const c = getColors(theme.palette.mode);
  const navigate = useNavigate();
  const [ref, width] = useWidth();
  const [hover, setHover] = useState<string | null>(null);

  const layout = useMemo(() => {
    const GAP = 6;
    const NODE_W = 10;
    const LABEL_W = Math.min(180, width * 0.24);
    const n = Math.max(flows.apps.length, flows.modules.length);
    const height = Math.max(300, n * 30);
    const total = flows.apps.reduce((s, a) => s + a.value, 0);
    const scale = total > 0 ? (height - GAP * (n - 1)) / total : 0;

    const stack = (nodes: typeof flows.apps, sideTotal: number) => {
      // Centre the shorter side vertically.
      const used = sideTotal * scale + GAP * (nodes.length - 1);
      let y = (height - used) / 2;
      const pos = new Map<string, { y: number; h: number }>();
      nodes.forEach(nd => {
        const h = nd.value * scale;
        pos.set(nd.id, { y, h });
        y += h + GAP;
      });
      return pos;
    };
    const appPos = stack(flows.apps, total);
    const modPos = stack(flows.modules, total);
    const xL = LABEL_W;
    const xR = width - LABEL_W - NODE_W;

    // Stack each link inside its two nodes, ordered by the far side's position.
    const appCursor = new Map(Array.from(appPos.entries()).map(([k, v]) => [k, v.y]));
    const modCursor = new Map(Array.from(modPos.entries()).map(([k, v]) => [k, v.y]));
    const appIndex = new Map(flows.apps.map((a, i) => [a.id, i]));
    const modIndex = new Map(flows.modules.map((m, i) => [m.id, i]));
    const links = [...flows.links]
      .sort((a, b) => appIndex.get(a.app)! - appIndex.get(b.app)! || modIndex.get(a.module)! - modIndex.get(b.module)!)
      .map(l => {
        const h = l.value * scale;
        const y0 = appCursor.get(l.app)!;
        appCursor.set(l.app, y0 + h);
        return { ...l, h, y0 };
      });
    [...links]
      .sort((a, b) => modIndex.get(a.module)! - modIndex.get(b.module)! || appIndex.get(a.app)! - appIndex.get(b.app)!)
      .forEach(l => {
        const y1 = modCursor.get(l.module)!;
        modCursor.set(l.module, y1 + l.h);
        (l as typeof l & { y1: number }).y1 = y1;
      });

    return { height, appPos, modPos, xL, xR, NODE_W, links: links as Array<(typeof links)[number] & { y1: number }> };
  }, [flows, width]);

  const { height, appPos, modPos, xL, xR, NODE_W, links } = layout;
  const touches = (l: { app: string; module: string }) => hover === null || hover === l.app || hover === l.module;
  const appLabel = new Map(flows.apps.map(a => [a.id, a.label]));
  const modLabel = new Map(flows.modules.map(m => [m.id, m.label]));

  return (
    <Box ref={ref} onMouseLeave={() => setHover(null)}>
      <svg width={width} height={height} role="img" aria-label="Apps and the modules their jobs call">
        {links.map(l => (
          <Tooltip key={`${l.app}>${l.module}`} arrow followCursor title={`${appLabel.get(l.app)} → ${modLabel.get(l.module)}: ${l.value} job${l.value === 1 ? '' : 's'}`}>
            <path
              d={band(xL + NODE_W, l.y0, l.y0 + l.h, xR, l.y1, l.y1 + l.h)}
              fill={touches(l) && hover !== null ? c.primary : c.inkFaint}
              opacity={touches(l) ? (hover === null ? 0.28 : 0.5) : 0.08}
              style={{ transition: 'opacity 120ms, fill 120ms' }}
            />
          </Tooltip>
        ))}
        {flows.apps.map(a => {
          const p = appPos.get(a.id)!;
          return (
            <g key={a.id} onMouseEnter={() => setHover(a.id)} style={{ cursor: 'default' }}>
              <rect x={xL} y={p.y} width={NODE_W} height={Math.max(2, p.h)} rx={2} fill={c.primary} />
              <text x={xL - 8} y={p.y + p.h / 2 + 4} textAnchor="end" fontSize={12} fill={c.ink} fontWeight={hover === a.id ? 700 : 500}>
                {truncate(a.label, 24)}
                <tspan fill={c.inkMuted} fontWeight={400}>{`  ${a.value}`}</tspan>
              </text>
            </g>
          );
        })}
        {flows.modules.map(m => {
          const p = modPos.get(m.id)!;
          const link = m.id !== OTHER_MODULES;
          return (
            <g
              key={m.id}
              onMouseEnter={() => setHover(m.id)}
              onClick={link ? () => navigate(modulePath(m.id)) : undefined}
              style={{ cursor: link ? 'pointer' : 'default' }}
            >
              <rect x={xR} y={p.y} width={NODE_W} height={Math.max(2, p.h)} rx={2} fill={c.primary} />
              <text x={xR + NODE_W + 8} y={p.y + p.h / 2 + 4} fontSize={12} fill={c.ink} fontWeight={hover === m.id ? 700 : 500}>
                {truncate(m.label, 26)}
                <tspan fill={c.inkMuted} fontWeight={400}>{`  ${m.value}`}</tspan>
              </text>
            </g>
          );
        })}
      </svg>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Composition graph: which modules call which
// ---------------------------------------------------------------------------

export const CompositionGraph: React.FC<{ edges: ModuleEdge[]; maxEdges?: number }> = ({ edges, maxEdges = 24 }) => {
  const theme = useTheme();
  const c = getColors(theme.palette.mode);
  const navigate = useNavigate();
  const [ref, width] = useWidth();
  const [hover, setHover] = useState<string | null>(null);

  const layout = useMemo(() => {
    const shown = edges.slice(0, maxEdges);
    const nodes = Array.from(new Set(shown.flatMap(e => [e.parent, e.child])));
    // Depth = longest path from a node with no parent (bounded, in case of a cycle).
    const depth = new Map<string, number>(nodes.map(n => [n, 0]));
    for (let pass = 0; pass < nodes.length; pass++) {
      let changed = false;
      shown.forEach(e => {
        const d = depth.get(e.parent)! + 1;
        if (d > depth.get(e.child)! && d < 6) {
          depth.set(e.child, d);
          changed = true;
        }
      });
      if (!changed) break;
    }
    const weight = new Map<string, number>();
    shown.forEach(e => {
      weight.set(e.parent, (weight.get(e.parent) || 0) + e.jobs);
      weight.set(e.child, (weight.get(e.child) || 0) + e.jobs);
    });
    const cols = Math.max(...Array.from(depth.values()), 0) + 1;
    const byCol: string[][] = Array.from({ length: cols }, () => []);
    nodes.forEach(n => byCol[depth.get(n)!].push(n));
    byCol.forEach(col => col.sort((a, b) => (weight.get(b) || 0) - (weight.get(a) || 0) || a.localeCompare(b)));

    const ROW = 36;
    const NODE_H = 26;
    const height = Math.max(...byCol.map(col => col.length), 1) * ROW + 8;
    const nodeW = Math.min(200, (width - 40 * (cols - 1)) / cols);
    const gapX = cols > 1 ? (width - nodeW * cols) / (cols - 1) : 0;
    const pos = new Map<string, { x: number; y: number }>();
    byCol.forEach((col, ci) => {
      const offset = (height - col.length * ROW) / 2;
      col.forEach((n, ri) => pos.set(n, { x: ci * (nodeW + gapX), y: offset + ri * ROW + (ROW - NODE_H) / 2 }));
    });
    const maxJobs = Math.max(1, ...shown.map(e => e.jobs));
    return { shown, nodes, pos, nodeW, NODE_H, height, maxJobs };
  }, [edges, maxEdges, width]);

  const { shown, nodes, pos, nodeW, NODE_H, height, maxJobs } = layout;
  const active = (e: ModuleEdge) => hover === null || hover === e.parent || hover === e.child;

  return (
    <Box ref={ref} onMouseLeave={() => setHover(null)}>
      <svg width={width} height={height} role="img" aria-label="Modules and the modules they call">
        <defs>
          <marker id="composition-arrow" viewBox="0 0 8 8" refX="7" refY="4" markerWidth="7" markerHeight="7" orient="auto">
            <path d="M0,0 L8,4 L0,8 Z" fill={c.inkMuted} />
          </marker>
        </defs>
        {shown.map(e => {
          const a = pos.get(e.parent)!;
          const b = pos.get(e.child)!;
          const x0 = a.x + nodeW;
          const y0 = a.y + NODE_H / 2;
          const x1 = b.x - 2;
          const y1 = b.y + NODE_H / 2;
          const xm = (x0 + x1) / 2;
          const on = active(e);
          return (
            <Tooltip key={`${e.parent}>${e.child}`} arrow followCursor title={`${moduleName(e.parent)} calls ${moduleName(e.child)} in ${e.jobs} job${e.jobs === 1 ? '' : 's'}`}>
              <path
                d={`M${x0},${y0} C${xm},${y0} ${xm},${y1} ${x1},${y1}`}
                fill="none"
                stroke={on && hover !== null ? c.primary : c.inkMuted}
                strokeWidth={1.5 + (e.jobs / maxJobs) * 5}
                strokeOpacity={on ? 0.55 : 0.1}
                markerEnd="url(#composition-arrow)"
                style={{ transition: 'stroke-opacity 120ms' }}
              />
            </Tooltip>
          );
        })}
        {nodes.map(n => {
          const p = pos.get(n)!;
          const on = hover === null || hover === n || shown.some(e => (e.parent === hover && e.child === n) || (e.child === hover && e.parent === n));
          return (
            <g
              key={n}
              onMouseEnter={() => setHover(n)}
              onClick={() => navigate(modulePath(n))}
              style={{ cursor: 'pointer', opacity: on ? 1 : 0.35, transition: 'opacity 120ms' }}
            >
              <rect x={p.x} y={p.y} width={nodeW} height={NODE_H} rx={6} fill={c.surface} stroke={hover === n ? c.primary : c.hairline} strokeWidth={hover === n ? 2 : 1} />
              <text x={p.x + 10} y={p.y + NODE_H / 2 + 4} fontSize={12} fontWeight={500} fill={c.ink}>
                {truncate(moduleName(n), Math.floor((nodeW - 20) / 6.6))}
              </text>
            </g>
          );
        })}
      </svg>
    </Box>
  );
};
